import { Grid2, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AuctionCatalogueStatusEnum } from '../../_common/enum/auctionCatalogueStatus.enum';
import { AuctionTypeEnum } from '../../_common/enum/auctionType.enum';
import { getDateFromString } from '../../_helpers/dateTime.helper';
import { handleErrorResponse } from '../../_helpers/handleResponse.helper';
import { useAuctionStatus } from '../../_hubs/auctionStatus.hub';
import { useCatalogueItemStatus } from '../../_hubs/catalogueItemStatus.hub';
import { accountService } from '../../_services/accounts.service';
import { auctionHeaderService } from '../../_services/auctionHeader.service';
import { auctionService } from '../../_services/auctionService.service';
import { catalogueItemService } from '../../_services/catalogueItemService.service';
import { AuctionAdminIndex } from './auction-admin';
import { AuctionMonitorIndex } from './auction-monitor';
import { AuctionProxyBidderIndex } from './auction-proxyBidder';
import { AuctionUserIndex } from './auction-userIndex';
import { AuctionAnnualRoleRoot } from './annual/auction-annual-role-root';

export function AuctionIndex() {

    const [auction, setAuction] = useState<any>(null);
    const [catalogueItem, setCatalogueItem] = useState<any>(null);
    const [loading, setLoading] = useState({ auctionLoading: true, catalogueItemLoading: true });
    const [proxyBidderId, setProxyBidderId] = useState<string>("");
    const [statusSyncPending, setStatusSyncPending] = useState(false);
    const syncTimerRef = useRef<number | null>(null);
    const reconnectTimerRef = useRef<number | null>(null);
    const auctionStatusRejoinRef = useRef<(() => Promise<void>) | null>(null);
    const catalogueStatusRejoinRef = useRef<(() => Promise<void>) | null>(null);

    // Default to the adhoc experience unless the auction is explicitly Annual, so existing
    // adhoc auctions (AuctionTypeId is nullable and may be unset on legacy rows) keep working.
    const isAnnualAuction = Number(auction?.auctionTypeId) === AuctionTypeEnum.Annual;
    const isAdhocAuction = !isAnnualAuction;

    // Surface the auction date/type in the global toolbar so the page top stays clean.
    useEffect(() => {
        auctionHeaderService.set({
            dateLabel: auction ? getDateFromString(auction.date) : null,
            typeLabel: auction ? (isAnnualAuction ? "Annual Auction" : "Live Auction") : "Live Auction",
        });
    }, [auction, isAnnualAuction]);

    useEffect(() => () => auctionHeaderService.reset(), []);

    const fetchActiveCatalogueItem = useCallback((auctionId?: number, options?: { silent?: boolean }) => {
        const id = auctionId ?? auction?.id;
        if (!id) return Promise.resolve();

        if (!options?.silent) {
            setLoading((l: any) => ({ ...l, catalogueItemLoading: true }));
        }

        return catalogueItemService.getActive(id, proxyBidderId)
            .then(
                _catalogueItem => {
                    setCatalogueItem(_catalogueItem);
                }, handleErrorResponse)
            .finally(() => {
                if (!options?.silent) {
                    setLoading((l: any) => ({ ...l, catalogueItemLoading: false }));
                }
            });
    }, [auction?.id, proxyBidderId]);

    const syncFromServer = useCallback(() => {
        if (!auction?.id) return;

        setStatusSyncPending(true);

        if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
        syncTimerRef.current = window.setTimeout(() => {
            syncTimerRef.current = null;
            const auctionId = auction.id;
            const syncSafetyTimer = window.setTimeout(() => setStatusSyncPending(false), 10_000);

            Promise.all([
                auctionService.getActive()
                    .then((nextAuction) => {
                        if (nextAuction) setAuction(nextAuction);
                    }, handleErrorResponse),
                catalogueItemService.getActive(auctionId, proxyBidderId)
                    .then((nextItem) => {
                        setCatalogueItem(nextItem);
                    }, handleErrorResponse),
            ]).finally(() => {
                window.clearTimeout(syncSafetyTimer);
                setStatusSyncPending(false);
            });
        }, 150);
    }, [auction?.id, proxyBidderId]);

    const onUserReconnected = useCallback(() => {
        if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);

        reconnectTimerRef.current = window.setTimeout(() => {
            reconnectTimerRef.current = null;

            Promise.all([
                auctionStatusRejoinRef.current?.(),
                catalogueStatusRejoinRef.current?.(),
            ]).finally(() => {
                syncFromServer();
            });
        }, 400);
    }, [syncFromServer]);

    useEffect(() => () => {
        if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
        if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
    }, []);

    const { auctionStatus } = useAuctionStatus(auction?.id, auctionStatusRejoinRef);
    const { catalogueItemStatus } = useCatalogueItemStatus(catalogueItem?.id, catalogueStatusRejoinRef);

    useEffect(() => {
        setTimeout(() => { fetchActiveAuction() }, 1000);
    }, []);

    useEffect(() => {
        if (
            isAdhocAuction &&
            (!!auction?.id || (!!proxyBidderId && accountService.isProxyBidder))
        ) {
            fetchActiveCatalogueItem();
            return;
        }

        if (!!auction?.id && !isAdhocAuction) {
            setCatalogueItem(null);
            setLoading((l: any) => ({ ...l, catalogueItemLoading: false }));
        }
    }, [auction?.id, proxyBidderId, isAdhocAuction, fetchActiveCatalogueItem]);

    useEffect(() => {
        if (!auctionStatus || auctionStatus.auctionStatusId == auction?.auctionStatusId) return;

        setAuction((prev: any) => ({
            ...prev,
            auctionStatusId: auctionStatus.auctionStatusId,
        }));
        fetchActiveCatalogueItem(undefined, { silent: true });
    }, [auctionStatus?.auctionStatusId, auction?.auctionStatusId, fetchActiveCatalogueItem]);

    useEffect(() => {
        if (!!catalogueItemStatus && !!catalogueItem && catalogueItem?.auctionCatalogueStatusId != catalogueItemStatus?.auctionCatalogueStatusId) {
            setCatalogueItem((_d: any) => {
                return {
                    ..._d, ...catalogueItemStatus
                }
            })
        }
    }, [catalogueItemStatus?.auctionCatalogueStatusId]);

    useEffect(() => {
        if (!!catalogueItem && !!catalogueItem?.auctionCatalogueStatusId && (catalogueItem.auctionCatalogueStatusId == AuctionCatalogueStatusEnum.Completed || catalogueItem.auctionCatalogueStatusId == AuctionCatalogueStatusEnum.Withdrawn)) {
            fetchActiveCatalogueItem();
        }
    }, [catalogueItem?.auctionCatalogueStatusId]);

    function fetchActiveAuction() {
        setLoading({ auctionLoading: true, catalogueItemLoading: true });

        return auctionService.getActive()
            .then(
                auction => {
                    setAuction(auction);
                }, handleErrorResponse)
            .finally(() => {
                setLoading((l: any) => ({ ...l, auctionLoading: false }));
            });
    }

    return (
        <>
            <div style={{ maxWidth: "1920px", margin: "0px auto" }}>
                <Grid2 container spacing={1}>
                    {(!!loading.auctionLoading || !auction) && (
                        <Grid2 size={{ xs: 12 }}>
                            <Typography variant="h4" textAlign={"center"}>{!!loading.auctionLoading ? "Loading.." : "No active or scheduled auctions"} </Typography>
                        </Grid2>
                    )}

                    {(!!auction?.id && isAdhocAuction) && <>
                        {
                            !!accountService.isAdmin || !!accountService.isAuctioneer ? <AuctionAdminIndex auction={auction} catalogueItem={catalogueItem} catalogueItemLoading={loading.catalogueItemLoading} /> :
                                !!accountService.isProxyBidder ? <AuctionProxyBidderIndex proxyBidderId={proxyBidderId} setProxyBidderId={setProxyBidderId} auction={auction} catalogueItem={catalogueItem} catalogueItemLoading={loading.catalogueItemLoading} /> :
                                    !!accountService.isMonitor ? <AuctionMonitorIndex auction={auction} catalogueItem={catalogueItem} catalogueItemLoading={loading.catalogueItemLoading} /> :
                                        <AuctionUserIndex auction={auction} catalogueItem={catalogueItem} catalogueItemLoading={loading.catalogueItemLoading} statusSyncPending={statusSyncPending} onResyncFromServer={onUserReconnected} />
                        }
                    </>
                    }

                    {(!!auction?.id && isAnnualAuction) &&
                        <AuctionAnnualRoleRoot auction={auction} />
                    }
                </Grid2>
            </div>
        </>
    );
}
